import {
  ErrorModal,
  useContextMenuMESF,
  useShiftNavigator,
} from "@dexteel/mesf-core";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Ganttv2,
  Task,
  calculateTotalGanttHeight,
  type Asset as GanttAsset,
} from "../../../../../controls/ganttAsset";
import { ConfirmationDialogRaw } from "../../../../../controls/modals/ConfirmationDialogRaw";
import { SplitDelays } from "../../../../delays/delays-manager/components/modals/SplitDelays";
import { UpsertJustifyDelay } from "../../../../delays/delays-manager/components/modals/UpsertJustifyDelay";
import { unLinkEAFProcess } from "../../../../eaf/eaf-processes/repositories/EAFRepository";
import { unLinkLMFProcess } from "../../../../lmf/lmf-processes/repositories/LMFRepository";
import type {
  Asset as DomainAsset,
  Process,
} from "../../../models/ProcessGanttChart";
import type { LMFProcessByShift } from "../../../models/ProcessByShift";
import type { SelectedProcess } from "../../models/SelectedProcess";
import {
  deleteCASProcess,
  deleteEAFProcess,
  deleteLMFProcess,
  unLinkProcess as unLinkCasterProcess,
} from "../../../repositories/ProductionRepository";
import { useGanttOptionsContextMenu } from "../../hooks/useGanttOptionsContextMenu";
import { LinkProcess } from "./LinkProcess";
import { UpsertProcess } from "./UpsertProcess";

interface Props {
  columnWidth: number;
  assets: GanttAsset[];
  showArrowTriangle: boolean;
  startCalendarDate: Date;
  endCalendarDate: Date;
  rowHeight: number;
  ganttHeight?: number;
  barFill: number;
  lineSectionFill: number;
  lineFill: number;
  lmfProcesses?: LMFProcessByShift[];
  ganttProcesses?: Process[];
  selectedProcess: SelectedProcess | null;
  setSelectedProcess: (process: SelectedProcess | null) => void;
  onError: (message: string) => void;
  onNotify: (message: string) => void;
  setIsLoadingBackground: (loading: boolean) => void;
}

export const GanttSection = ({
  columnWidth,
  assets,
  showArrowTriangle,
  startCalendarDate,
  endCalendarDate,
  rowHeight,
  ganttHeight,
  barFill,
  lineSectionFill,
  lineFill,
  lmfProcesses = [],
  ganttProcesses = [],
  selectedProcess,
  setSelectedProcess,
  onError,
  onNotify,
  setIsLoadingBackground,
}: Props) => {
  const navigate = useNavigate();
  const { shiftInfo } = useShiftNavigator();

  const [selectedAsset, setSelectedAsset] = useState<DomainAsset | null>(null);
  const [comparisonDate, setComparisonDate] = useState<Date | null>(null);
  const [delayId, setDelayId] = useState<number | null>(null);
  const [assetId, setAssetId] = useState<number | null>(null);

  const {
    getGanttMenuOptions,
    getGanttDelaysMenuOptions,
    showModal,
    showModalDelay,
    messageDeleteModal,
    messageUnlinkModal,
    setShowModal,
    setShowModalDelay,
    setMessageDeleteModal,
    setMessageUnlinkModal,
    setError,
    error,
  } = useGanttOptionsContextMenu({ selectedAsset, setSelectedProcess });

  const { showContextMenu, registerConfig } = useContextMenuMESF();

  const handleDoubleClick = (task: Task) => {
    if (task.type === "taskLine" && task.source.eventCode === "D") {
      setAssetId(Number(task.source.id));
      setDelayId(task.source.DelayId);
      setShowModalDelay("justify");
    } else {
      selectProcessGantt(task);
    }
  };

  const selectProcessGantt = (process: Task) => {
    const source = process.source as Process;
    let assetType = "";
    let treatmentNumber: number | undefined = undefined;
    switch (source.ProcessCode) {
      case "C":
        assetType = "caster";
        break;
      case "L":
        assetType = "lmf";
        if (lmfProcesses.length > 0) {
          const treatment = lmfProcesses.find(
            (t) => t.HeatId === source.HeatId,
          );
          if (
            treatment &&
            typeof treatment.TreatmentNumber === "number" &&
            treatment.TreatmentNumber !== null
          ) {
            treatmentNumber = treatment.TreatmentNumber;
          }
        }
        break;
      case "E":
        assetType = "eaf";
        break;
      default:
        assetType = "eaf";
    }

    const params = new URLSearchParams({
      heatId: String(source.HeatId),
      processId: String(source.ProcessId),
    });
    if (typeof treatmentNumber === "number") {
      params.append("treatmentNumber", String(treatmentNumber));
    }
    navigate(`/heat-process-report/${assetType}?${params.toString()}`);
  };

  const handleUnlinkProcess = async (value?: string) => {
    setShowModal("");
    setMessageUnlinkModal("");
    if (value !== "OK") return;
    if (selectedProcess === null) return;
    setIsLoadingBackground(true);

    try {
      const unlinkProcess =
        selectedProcess.ProcessCode === "E"
          ? unLinkEAFProcess
          : selectedProcess.ProcessCode === "L"
            ? unLinkLMFProcess
            : unLinkCasterProcess;

      const resp = await unlinkProcess(selectedProcess.ProcessId);
      if (resp.ok) {
        onNotify("Process unlinked successfully");
      } else {
        onError(resp.message);
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to unlink process");
    } finally {
      setIsLoadingBackground(false);
    }
  };
  const handleDeleteProcess = async (value?: string) => {
    setShowModal("");
    setMessageDeleteModal("");
    if (value !== "OK") return;
    if (selectedProcess === null) return;
    setIsLoadingBackground(true);

    try {
      const resp =
        selectedProcess.ProcessCode === "E"
          ? await deleteEAFProcess(selectedProcess.ProcessId)
          : selectedProcess.ProcessCode === "L"
            ? await deleteLMFProcess(selectedProcess.ProcessId)
            : await deleteCASProcess(selectedProcess.ProcessId);
      if (resp.ok) {
        onNotify("Process deleted successfully");
      } else {
        onError(resp.message);
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to delete process");
    } finally {
      setIsLoadingBackground(false);
    }
  };

  // Use refs so registered callbacks always call the latest version (avoids stale closures)
  const ganttMenuRef = useRef(getGanttMenuOptions);
  ganttMenuRef.current = getGanttMenuOptions;
  const ganttDelaysMenuRef = useRef(getGanttDelaysMenuOptions);
  ganttDelaysMenuRef.current = getGanttDelaysMenuOptions;

  useEffect(() => {
    registerConfig({
      id: "ganttSection",
      getOptions: (...args: Parameters<typeof getGanttMenuOptions>) =>
        ganttMenuRef.current(...args),
    });
    registerConfig({
      id: "ganttSectionDelaysMenu",
      getOptions: (...args: Parameters<typeof getGanttDelaysMenuOptions>) =>
        ganttDelaysMenuRef.current(...args),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- registerConfig is an unstable reference from ContextMenuMESFProvider (not wrapped in useCallback). Including it causes an infinite loop.
  }, []);

  const calculatedGanttHeight =
    ganttHeight || calculateTotalGanttHeight(assets, rowHeight);

  return (
    <>
      <Ganttv2
        columnWidth={columnWidth}
        assets={assets}
        showArrowTriangle={showArrowTriangle}
        startCalendarDate={startCalendarDate}
        endCalendarDate={endCalendarDate}
        rowHeight={rowHeight}
        ganttHeight={calculatedGanttHeight}
        barFill={barFill}
        lineSectionFill={lineSectionFill}
        lineFill={lineFill}
        onDoubleClick={handleDoubleClick}
        onContextMenu={(event: any, data: any) => {
          if (data.timestamp) {
            setComparisonDate(data.timestamp);
          }
          if (data.type === "asset") {
            setSelectedAsset(data.asset);
          }
          if (data.type !== "taskLine") {
            showContextMenu(event, data, "ganttSection");
          }
          if (data.type === "taskLine" && data.eventCode === "D") {
            setAssetId(Number(data.id));
            setDelayId(data.DelayId);
            showContextMenu(event, data, "ganttSectionDelaysMenu");
          }
        }}
      />
      <ConfirmationDialogRaw
        title="Confirmation"
        message={messageUnlinkModal}
        open={showModal === "unlink"}
        onClose={handleUnlinkProcess}
      />
      <ConfirmationDialogRaw
        title="Confirmation"
        message={messageDeleteModal}
        open={showModal === "delete"}
        onClose={handleDeleteProcess}
      />
      <LinkProcess
        show={showModal === "link"}
        onHide={() => setShowModal("")}
        process={selectedProcess as Process | null}
        onNotify={onNotify}
      />
      <UpsertProcess
        show={showModal === "edit"}
        onHide={() => setShowModal("")}
        comparisonDate={comparisonDate}
        ganttProcesses={ganttProcesses}
        selectedProcess={selectedProcess as Process | null}
        selectedAsset={selectedAsset}
        onError={onError}
        onNotify={onNotify}
      />
      <UpsertJustifyDelay
        showModal={showModalDelay}
        onHide={() => {
          setShowModalDelay("");
          setDelayId(null);
        }}
        delayId={delayId}
        assetId={assetId}
      />
      <SplitDelays
        show={showModalDelay === "split"}
        onHide={() => {
          setShowModalDelay("");
          setDelayId(null);
        }}
        delayId={delayId}
        assetId={assetId ?? null}
      />
      <ErrorModal error={error} onHide={() => setError("")} />
    </>
  );
};
